import { useRef, useState, useEffect, useCallback } from "react";
import { motion, useInView } from "framer-motion";
import "./AnimatedList.scss";

const AnimatedItem = ({
    children,
    delay = 0,
    index,
    onMouseEnter,
    onClick,
}) => {
    const ref = useRef(null);
    const inView = useInView(ref, { amount: 0.5, triggerOnce: false });

    return (
        <motion.div
            ref={ref}
            data-index={index}
            onMouseEnter={onMouseEnter}
            onClick={onClick}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={
                inView ? { scale: 1, opacity: 1 } : { scale: 0.9, opacity: 0 }
            }
            transition={{ duration: 0.25, ease: "easeOut" }}
        >
            {children}
        </motion.div>
    );
};

/* ---------------- AnimatedList ---------------- */
const AnimatedList = ({
    items = [],
    renderItem,
    onItemSelect,
    showGradients = true,
    enableArrowNavigation = true,
    className = "",
    itemClassName = "",
    displayScrollbar = true,
    initialSelectedIndex = -1,
}) => {
    const listRef = useRef(null);
    const [selectedIndex, setSelectedIndex] = useState(initialSelectedIndex);
    const [keyboardNav, setKeyboardNav] = useState(false);
    const [topGradientOpacity, setTopGradientOpacity] = useState(0);
    const [bottomGradientOpacity, setBottomGradientOpacity] = useState(1);

    const handleItemMouseEnter = useCallback((index) => {
        setSelectedIndex(index);
    }, []);

    const handleItemClick = useCallback(
        (item, index) => {
            setSelectedIndex(index);
            onItemSelect?.(item, index);
        },
        [onItemSelect]
    );

    const handleScroll = useCallback((e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        setTopGradientOpacity(Math.min(scrollTop / 50, 1));

        const bottomDistance = scrollHeight - (scrollTop + clientHeight);
        setBottomGradientOpacity(
            scrollHeight <= clientHeight ? 0 : Math.min(bottomDistance / 50, 1)
        );
    }, []);

    /* 키보드 네비 */
    useEffect(() => {
        if (!enableArrowNavigation) return;

        const handleKeyDown = (e) => {
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setKeyboardNav(true);
                setSelectedIndex((prev) =>
                    Math.min(prev + 1, items.length - 1)
                );
            }

            if (e.key === "ArrowUp") {
                e.preventDefault();
                setKeyboardNav(true);
                setSelectedIndex((prev) => Math.max(prev - 1, 0));
            }

            if (e.key === "Enter" && selectedIndex >= 0) {
                e.preventDefault();
                onItemSelect?.(items[selectedIndex], selectedIndex);
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [items, selectedIndex, onItemSelect, enableArrowNavigation]);

    /* 선택 아이템 자동 스크롤 */
    useEffect(() => {
        if (!keyboardNav || selectedIndex < 0 || !listRef.current) return;

        const container = listRef.current;
        const selectedItem = container.querySelector(
            `[data-index="${selectedIndex}"]`
        );

        if (selectedItem) {
            const extraMargin = 50;
            const itemTop = selectedItem.offsetTop;
            const itemBottom = itemTop + selectedItem.offsetHeight;

            if (itemTop < container.scrollTop + extraMargin) {
                container.scrollTo({
                    top: itemTop - extraMargin,
                    behavior: "smooth",
                });
            } else if (
                itemBottom >
                container.scrollTop + container.clientHeight - extraMargin
            ) {
                container.scrollTo({
                    top: itemBottom - container.clientHeight + extraMargin,
                    behavior: "smooth",
                });
            }
        }

        setKeyboardNav(false);
    }, [selectedIndex, keyboardNav]);

    return (
        <div className={`scroll-list-container ${className}`}>
            <div
                ref={listRef}
                className={`scroll-list ${
                    !displayScrollbar ? "no-scrollbar" : ""
                }`}
                onScroll={handleScroll}
            >
                {items.map((item, index) => (
                    <AnimatedItem
                        key={item?.id ?? index}
                        delay={0.1}
                        index={index}
                        onMouseEnter={() => handleItemMouseEnter(index)}
                        onClick={() => handleItemClick(item, index)}
                    >
                        <div
                            className={`item ${
                                selectedIndex === index ? "selected" : ""
                            } ${itemClassName}`}
                        >
                            {renderItem ? (
                                renderItem(item, index)
                            ) : (
                                <p className="item-text">{item}</p>
                            )}
                        </div>
                    </AnimatedItem>
                ))}
            </div>

            {showGradients && (
                <>
                    <div
                        className="top-gradient"
                        style={{ opacity: topGradientOpacity }}
                    />
                    <div
                        className="bottom-gradient"
                        style={{ opacity: bottomGradientOpacity }}
                    />
                </>
            )}
        </div>
    );
};

export default AnimatedList;
